import type { ReactGridLayoutProps } from 'react-grid-layout';
import type { Except } from 'type-fest';

import { APP_INFO } from '@ricoh/ria.client.core/constants/core/app';
import { COMPONENT_SIZE, LAYOUT_SPACER } from '@ricoh/ria.client.core/constants/ui/layout';
import { newStaticId } from '@ricoh/ria.client.core/utils/data/key';

import type {
  DocumentNotificationType,
  FieldDataSourceType,
  FieldFormat,
  MetaDataExtractionType,
  MetaDataType,
  PatternValueSystemValueBatch,
  PatternValueSystemValueDocument,
  PatternValueSystemValueDocumentFile,
  PatternValueSystemValueGenerate,
  RequirementType,
  SlaFormat,
  SlaType,
  Stamping,
  StampingPlacement,
  StampingRotation,
  Variable,
} from '~/common/model/api/batch-type';
import type { BusinessRuleModifyStringOperation, BusinessRuleType } from '~/common/model/api/batch-type/business-rule';
import type { LayoutItem } from '~/common/model/api/batch-type/document-layout';
import type {
  PatternGenerateNowType,
  TransformationDateTime,
  TransformationEvalExpressionCalculateOperator,
  TransformationEvalExpressionDateUnit,
  TransformationFormat,
  TransformationRegexOperation,
} from '~/common/model/api/batch-type/pattern';
import type {
  BatchOptions,
  DocumentOptions,
  ProcessFlowStepActionSeparationType,
  ProcessFlowStepActionType,
  ProcessFlowStepConditionDocumentRule,
  ProcessFlowStepConditionType,
  ProcessFlowStepConnectorType,
  ProcessFlowStepTaskType,
  ProcessFlowStepType,
  RoutingFileType,
} from '~/common/model/api/batch-type/process-flow';
import type { DataTypeName, DataTypeNameGeneral } from '~/common/model/api/common';
import type { IntegrationTypeImport, IntegrationTypeRouting } from '~/common/model/api/integration';

export * from '~assets/code-generator/datatype-format-samples';

/** Batch Options. */
export const BATCH_OPTIONS: BatchOptions[] = ['none', 'openByDefault', 'offline'];

/** Document Options. */
export const DOCUMENT_OPTIONS: DocumentOptions[] = [
  'none',
  'classification',
  'assembly',
  'delete',
  'duplicate',
  'edit',
  'reassignment',
  'rejection',
  'scanning',
  'upload',
  'priority',
];

/** NamingRule SystemValue Generates. */
export const PATTERN_VALUE_SYSTEM_VALUES_GENERATE: PatternValueSystemValueGenerate[] = ['date', 'time', 'datetime', 'guid', 'random'];

/** PatternValue SystemValue Batch. */
export const PATTERN_VALUE_SYSTEM_VALUES_BATCH: PatternValueSystemValueBatch[] = [
  'batchId',
  'batchName',
  'batchType',
  'batchPriority',
  'documentCount',
  'createdBy',
  'createdByEmail',
  'createdAt',
  'createdAtDate',
  'createdAtTime',
  'lastUpdatedBy',
  'lastUpdatedByEmail',
  'lastUpdatedAt',
  'lastUpdatedAtDate',
  'lastUpdatedAtTime',
];

/** PatternValue SystemValue Document. */
export const PATTERN_VALUE_SYSTEM_VALUES_DOCUMENT: PatternValueSystemValueDocument[] = [
  'documentName',
  'documentType',
  'separationConfidence',
  'classificationConfidence',
  'extractionConfidence',
];

/** PatternValue SystemValue Document File. */
export const PATTERN_VALUE_SYSTEM_VALUES_DOCUMENT_FILE: PatternValueSystemValueDocumentFile[] = ['filePdfBase64'];

/** DataType General Names. */
export const DATA_TYPE_GENERAL_NAMES: DataTypeNameGeneral[] = ['text', 'integer', 'decimal', 'currency', 'boolean', 'datetime', 'date', 'time'];

/** DataType names. */
export const DATA_TYPE_NAMES: DataTypeName[] = [
  'text',
  'decimal',
  'currency',
  'integer',
  'boolean',
  'datetime',
  'date',
  'time',
  'list.text',
  'list.decimal',
  'list.currency',
  'list.integer',
  'list.boolean',
  'list.datetime',
  'list.date',
  'list.time',
  'table',
];

/** Field Format Text Types. */
export const FIELD_FORMAT_TEXT_TYPES: FieldFormat[] = ['short', 'long', 'longFit'];

/** SLA formats. */
export const SLA_FORMATS: SlaFormat[] = ['minute', 'hour', 'day'];

/** SLA types. */
export const SLA_TYPES: SlaType[] = ['green', 'amber', 'red'];

/** Sla notification formats. */
export const DOCUMENT_NOTIFICATION_TYPES: DocumentNotificationType[] = ['hourly', 'perDocument'];

/** Default Stamping. */
export const STAMPING_DEFAULT: Except<Stamping, 'id'> = {
  name: 'Stamp',
  stampingRule: [],
  style: {
    $is: 'text',
    placement: 'topLeft',
    rotation: 'noRotation',
    fontName: 'Arial',
    fontSize: 13,
    fontColor: '#ff0000',
  },
};

/** Stamping positions. */
export const STAMPING_POSITIONS: StampingPlacement[] = [
  'topLeft',
  'topCenter',
  'topRight',
  'centerLeft',
  'centerRight',
  'bottomLeft',
  'bottomCenter',
  'bottomRight',
];

/** Stamping rotation. */
export const STAMPING_ROTATIONS: StampingRotation[] = ['noRotation', 'clockwise', 'counterclockwise', 'upsideDown'];

/** Stamping fonts. */
export const STAMPING_FONTS: string[] = ['Arial', 'Comic Sans MS', 'Courier New', 'Georgia', 'Impact', 'Times New Roman', 'Trebuchet MS', 'Verdana'];

/** Requirement types. */
export const REQUIREMENT_TYPES: RequirementType[] = ['required', 'optional', 'hidden'];

/** DataSource types. */
export const DATA_SOURCE_TYPES: FieldDataSourceType[] = ['manual', 'auto', 'ai'];

/** Business Rule types. */
export const BUSINESS_RULE_TYPES: BusinessRuleType[] = [
  'calculateValue',
  'calculateDurationBetweenDates',
  'convertValue',
  'executeRegularExpression',
  'modifyString',
  'setVariable',
  'setField',
  'queryDatabase',
];

/** Operator Calculate. */
export const OPERATOR_CALCULATES: TransformationEvalExpressionCalculateOperator[] = ['add', 'sub', 'mul', 'div'];

/** Unit types. */
export const UNIT_TYPES: TransformationEvalExpressionDateUnit[] = ['days', 'hours', 'minutes'];

/** Target types. */
export const TARGET_TYPES: DataTypeNameGeneral[] = ['text', 'boolean', 'datetime', 'decimal'];

/** Operation types. */
export const OPERATION_TYPES: TransformationRegexOperation[] = ['is_match', 'match', 'replace', 'split'];

/** Operation Modify String types. */
export const OPERATION_MODIFY_STRING_TYPES: BusinessRuleModifyStringOperation[] = ['replace', 'find', 'trim', 'extractStartString', 'extractEndString'];

/** System Variables. */
export const SYSTEM_VARIABLES: Variable[] = [
  {
    id: '1',
    isSystemVariable: true,
    name: 'CreatedBy',
    displayName: 'Created by',
    type: {
      name: 'text',
    },
  },
  {
    id: '2',
    isSystemVariable: true,
    name: 'CreatedAt',
    displayName: 'Created date',
    type: {
      name: 'datetime',
    },
  },
  {
    id: '3',
    name: 'LastUpdatedBy',
    displayName: 'Last modified by',
    isSystemVariable: true,
    type: {
      name: 'text',
    },
  },
  {
    id: '4',
    name: 'LastUpdatedAt',
    displayName: 'Last modified date',
    isSystemVariable: true,
    type: {
      name: 'datetime',
    },
  },
  {
    id: '5',
    name: 'SeparationType',
    displayName: 'Separation type',
    isSystemVariable: true,
    type: {
      name: 'text',
    },
  },
  {
    id: '6',
    name: 'SeparationValue',
    displayName: 'Separation value',
    isSystemVariable: true,
    type: {
      name: 'text',
    },
  },
];

/** Batch Variables. */
export const BATCH_VARIABLES: Variable[] = [
  {
    id: '1',
    isSystemVariable: true,
    name: 'CreatedBy',
    displayName: 'Created by',
    type: {
      name: 'text',
    },
  },
  {
    id: '2',
    isSystemVariable: true,
    name: 'CreatedAt',
    displayName: 'Created date',
    type: {
      name: 'datetime',
    },
  },
  {
    id: '3',
    name: 'LastUpdatedBy',
    displayName: 'Last modified by',
    isSystemVariable: true,
    type: {
      name: 'text',
    },
  },
  {
    id: '4',
    name: 'LastUpdatedAt',
    displayName: 'Last modified date',
    isSystemVariable: true,
    type: {
      name: 'datetime',
    },
  },
];

/** Transformation DataTime. */
export const TRANSFORMATION_DATE_TIME: Record<PatternGenerateNowType, TransformationDateTime> = {
  datetime: {
    kind: '$datetime',
    format: 'yyyyMMddHHmmss',
  },
  date: {
    kind: '$datetime',
    format: 'yyyyMMdd',
  },
  time: {
    kind: '$datetime',
    format: 'HHmmss',
  },
};

/** Transformation Text. */
export const TRANSFORMATION_TEXT: TransformationFormat = {
  kind: '$format',
};

export const DOCUMENT_VALIDATION_DROPPING_ITEM: NonNullable<ReactGridLayoutProps['droppingItem']> = {
  i: newStaticId(APP_INFO.WEB.ID, 'droppingItem'),
  w: 3,
  h: 1,
};

export const DOCUMENT_VALIDATION_HINT_ITEM: LayoutItem = {
  i: newStaticId(APP_INFO.WEB.ID, 'hintItem'),
  x: 0,
  y: 1,
  w: 12,
  h: 1,
  static: true,
  moved: false,
};

export const DOCUMENT_VALIDATION_LAYOUT_PROPS: Partial<ReactGridLayoutProps> = {
  cols: 12,
  rowHeight: COMPONENT_SIZE.INPUT.LABEL + COMPONENT_SIZE.INPUT.HEIGHT + LAYOUT_SPACER.PX[1] * 3 + LAYOUT_SPACER.PX[2] * 2,
  resizeHandles: ['w', 'e'],
  droppingItem: DOCUMENT_VALIDATION_DROPPING_ITEM,
  draggableHandle: '.draggable-handle',
  draggableCancel: '.draggable-cancel',
  margin: [LAYOUT_SPACER.PX[2], LAYOUT_SPACER.PX[2]],
  isDroppable: true,
  isDraggable: true,
  isResizable: true,
  preventCollision: true,
};

/** ProcessFlow StepTypes. */
export const PROCESS_FLOW_STEP_TYPES: ProcessFlowStepType[] = ['task', 'businessRule', 'condition', 'connector', 'action'];

/** ProcessFlow Step Task Types. */
export const PROCESS_FLOW_STEP_TASK_TYPES: ProcessFlowStepTaskType[] = [
  'newBatch',
  'import',
  'review',
  'validation',
  'confirmation',
  // 'exception',
  // 'approval',
  'routing',
];

/** ProcessFlow Step Condition Types. */
export const PROCESS_FLOW_STEP_CONDITION_TYPES: ProcessFlowStepConditionType[] = ['boolean', 'branch'];

/** ProcessFlow Step Condition DocumentRules. */
export const PROCESS_FLOW_STEP_CONDITION_DOCUMENT_RULES: ProcessFlowStepConditionDocumentRule[] = ['all', 'any'];

/** ProcessFlow Step Connector Types. */
export const PROCESS_FLOW_STEP_CONNECTOR_TYPES: ProcessFlowStepConnectorType[] = ['separation', 'classification', 'extraction', 'rcip'];

/** ProcessFlow Step Action Types. */
export const PROCESS_FLOW_STEP_ACTION_TYPES: ProcessFlowStepActionType[] = ['splitBatch', 'imageEnhancement', 'detectBarcode'];

/** ProcessFlow Step Action Separation Types. */
export const PROCESS_FLOW_STEP_ACTION_SEPARATION_TYPES: ProcessFlowStepActionSeparationType[] = ['none', 'batch', 'document'];

/** ProcessFlow PageSize. */
export const PROCESS_FLOW_PAGE_SIZE = {
  WIDTH: 210,
  HEIGHT: 297,
} as const;

/** ProcessFlow PageScale. */
export const PROCESS_FLOW_PAGE_SCALE = {
  DEFAULT: 1.45,
} as const;

/** ProcessFlow Node Color. */
export const PROCESS_FLOW_NODE_COLOR = {
  start: 'gray-500',
  end: 'primary',
  step: {
    task: 'pink-500',
    businessRule: 'yellow-500',
    condition: 'blue-500',
    connector: 'teal-500',
    action: 'indigo-500',
    default: 'gray-500',
  },
} as const;

/** ProcessFlow node distance. */
export const PROCESS_FLOW_NODE_DISTANCE = 200;

/** ProcessFlow node dot size. */
export const PROCESS_FLOW_NODE_DOT_SIZE = 20;

/** ProcessFlow node step size. */
export const PROCESS_FLOW_NODE_STEP_SIZE = {
  MIN_WIDTH: 200,
  MAX_WIDTH: 400,

  MIN_HEIGHT: 100,
} as const;

/** RoutingSettings IntegrationTypes. */
export const ROUTING_SETTINGS_INTEGRATION_TYPES: IntegrationTypeRouting[] = ['microsoft', 'awsS3Bucket', 'sftp', 'docuware'];

/** ImportSettings IntegrationTypes. */
export const IMPORT_SETTINGS_INTEGRATION_TYPES: IntegrationTypeImport[] = ['microsoft', 'outlook', 'sftp'];

/** Metadata Extraction. */
export const META_DATA_EXTRACTION: MetaDataExtractionType[] = [
  'from',
  'message_id',
  'body_content',
  'subject',
  'to_recipients',
  'cc_recipients',
  'attachments_count',
  'message_importance',
  'received_date_time',
  'parent_folder',
  'flag_status',
  'file_name',
  'file_size',
];

/** Metadata Data Value. */
export const METADATA_CREATE_WITH_DATA_VALUE: MetaDataType[] = ['has_attachments', 'flag_status', 'message_importance'];

/** RoutingSettings FileTypes. */
export const ROUTING_SETTINGS_FILE_TYPES: RoutingFileType[] = ['pdf', 'tiff', 'csv', 'json', 'xml', 'txt'];

/** RoutingSettings Separators. */
export const ROUTING_SETTINGS_SEPARATOR_TYPES = {
  tab: '\\t',
  comma: ',',
  semicolon: ';',
  pipe: '|',
} as const;
